/**
 * Sprint 24 — RegisterMcpServerModal.
 *
 * The Custom MCP registration flow surfaced from the Add menu. A single
 * structured form (name → URL → auth) that registers via `add_connection` —
 * which tests + discovers tools automatically. OAuth connections register here;
 * each member authorises individually later (Sprint 7.2 per-user OAuth).
 */

import { useEffect, useState, type CSSProperties } from "react";
import { useAddConnection } from "../../../hooks/use-connections";
import { showToast } from "../primitives";

type AuthType = "none" | "bearer" | "header" | "oauth2";

export interface RegisterMcpServerModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (connectionId: string) => void;
  /**
   * Sprint 24.15.2 — pre-fill the connection name (e.g. the equipment kind from
   * a hire's "install this" CTA) so the new connection matches by kind.
   */
  seedName?: string;
}

export function RegisterMcpServerModal({ open, onClose, onCreated, seedName }: RegisterMcpServerModalProps) {
  const add = useAddConnection();
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [authType, setAuthType] = useState<AuthType>("none");
  const [authHeader, setAuthHeader] = useState("");
  const [authValue, setAuthValue] = useState("");
  const [oauthClientId, setOauthClientId] = useState("");
  const [oauthClientSecret, setOauthClientSecret] = useState("");
  const [oauthAuthorizeUrl, setOauthAuthorizeUrl] = useState("");
  const [oauthTokenUrl, setOauthTokenUrl] = useState("");
  const [oauthScopes, setOauthScopes] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Sprint 24.15.2 — seed the name field when opened from a hire install CTA.
  useEffect(() => {
    if (open && seedName) setName(seedName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, seedName]);

  if (!open) return null;

  const canSubmit =
    name.trim().length > 0 &&
    /^https:\/\/.+/.test(url.trim()) &&
    (authType !== "header" || authHeader.trim().length > 0) &&
    (authType === "none" || authType === "oauth2" || authValue.trim().length > 0) &&
    (authType !== "oauth2" ||
      (oauthClientId.trim() && oauthAuthorizeUrl.trim() && oauthTokenUrl.trim()));

  function reset() {
    setName("");
    setUrl("");
    setAuthType("none");
    setAuthHeader("");
    setAuthValue("");
    setOauthClientId("");
    setOauthClientSecret("");
    setOauthAuthorizeUrl("");
    setOauthTokenUrl("");
    setOauthScopes("");
    setError(null);
  }

  function close() {
    reset();
    onClose();
  }

  async function submit() {
    setError(null);
    try {
      const conn = await add.mutateAsync({
        name: name.trim(),
        url: url.trim(),
        auth_type: authType,
        ...(authType === "header" ? { auth_header: authHeader.trim() } : {}),
        ...(authType === "bearer" || authType === "header" ? { auth_value: authValue.trim() } : {}),
        ...(authType === "oauth2"
          ? {
              oauth_client_id: oauthClientId.trim(),
              oauth_client_secret: oauthClientSecret.trim() || undefined,
              oauth_authorization_url: oauthAuthorizeUrl.trim(),
              oauth_token_url: oauthTokenUrl.trim(),
              oauth_scopes: oauthScopes.trim() || undefined,
              oauth_pkce: true,
            }
          : {}),
      });
      showToast(`Connected ${conn.name}`, "success");
      const id = conn.connection_id;
      reset();
      onCreated(id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to register connection");
    }
  }

  return (
    <div style={backdrop} onClick={close}>
      <div style={cardModal} onClick={(e) => e.stopPropagation()}>
        <div style={headerRow}>
          <div>
            <h2 style={h2}>Register an MCP server</h2>
            <p style={tagline}>Connect any MCP-compatible server. We'll test it and discover its tools.</p>
          </div>
          <button type="button" style={closeBtn} onClick={close} aria-label="Close">
            <i className="ti ti-x" />
          </button>
        </div>

        <label style={field}>
          <span style={labelText}>Name</span>
          <input
            style={input}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Company API"
          />
        </label>

        <label style={field}>
          <span style={labelText}>Server URL</span>
          <input
            style={input}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://mcp.example.com"
          />
        </label>

        <label style={field}>
          <span style={labelText}>Authentication</span>
          <select style={input} value={authType} onChange={(e) => setAuthType(e.target.value as AuthType)}>
            <option value="none">None</option>
            <option value="bearer">Bearer token</option>
            <option value="header">Custom header</option>
            <option value="oauth2">OAuth 2.0</option>
          </select>
        </label>

        {authType === "header" && (
          <label style={field}>
            <span style={labelText}>Header name</span>
            <input style={input} value={authHeader} onChange={(e) => setAuthHeader(e.target.value)} placeholder="X-API-Key" />
          </label>
        )}

        {(authType === "bearer" || authType === "header") && (
          <label style={field}>
            <span style={labelText}>{authType === "bearer" ? "Token" : "Header value"}</span>
            <input style={input} type="password" value={authValue} onChange={(e) => setAuthValue(e.target.value)} placeholder="••••••••" />
          </label>
        )}

        {authType === "oauth2" && (
          <>
            <label style={field}>
              <span style={labelText}>Client ID</span>
              <input style={input} value={oauthClientId} onChange={(e) => setOauthClientId(e.target.value)} />
            </label>
            <label style={field}>
              <span style={labelText}>Client secret (optional with PKCE)</span>
              <input style={input} type="password" value={oauthClientSecret} onChange={(e) => setOauthClientSecret(e.target.value)} />
            </label>
            <label style={field}>
              <span style={labelText}>Authorization URL</span>
              <input style={input} value={oauthAuthorizeUrl} onChange={(e) => setOauthAuthorizeUrl(e.target.value)} placeholder="https://…/authorize" />
            </label>
            <label style={field}>
              <span style={labelText}>Token URL</span>
              <input style={input} value={oauthTokenUrl} onChange={(e) => setOauthTokenUrl(e.target.value)} placeholder="https://…/token" />
            </label>
            <label style={field}>
              <span style={labelText}>Scopes (space-separated)</span>
              <input style={input} value={oauthScopes} onChange={(e) => setOauthScopes(e.target.value)} placeholder="read write" />
            </label>
          </>
        )}

        {error && <div style={errorBox}>{error}</div>}

        <div style={footer}>
          <button type="button" style={cancelBtn} onClick={close}>
            Cancel
          </button>
          <button
            type="button"
            style={{ ...primaryBtn, opacity: canSubmit && !add.isPending ? 1 : 0.5 }}
            disabled={!canSubmit || add.isPending}
            onClick={submit}
          >
            {add.isPending ? "Registering…" : "Register & test"}
          </button>
        </div>
      </div>
    </div>
  );
}

const backdrop: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 100,
  padding: 20,
  overflowY: "auto",
};

const cardModal: CSSProperties = {
  background: "var(--color-background-primary)",
  border: "0.5px solid var(--color-border-tertiary)",
  borderRadius: "var(--border-radius-lg)",
  width: 460,
  maxWidth: "90vw",
  padding: "22px 24px",
  display: "flex",
  flexDirection: "column",
  gap: 12,
  fontFamily: "var(--font-sans)",
  boxShadow: "0 12px 40px rgba(0,0,0,0.18)",
  margin: "auto",
};

const headerRow: CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 12,
  marginBottom: 4,
};

const h2: CSSProperties = { fontSize: 18, fontWeight: 500, margin: 0, color: "var(--color-text-primary)" };
const tagline: CSSProperties = { fontSize: 12, color: "var(--color-text-secondary)", margin: "4px 0 0" };

const closeBtn: CSSProperties = {
  background: "transparent",
  border: "none",
  cursor: "pointer",
  color: "var(--color-text-tertiary)",
  fontSize: 16,
  padding: 2,
  flexShrink: 0,
};

const field: CSSProperties = { display: "flex", flexDirection: "column", gap: 4 };

const labelText: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  color: "var(--color-text-secondary)",
};

const input: CSSProperties = {
  fontSize: 13,
  padding: "7px 10px",
  borderRadius: "var(--border-radius-sm)",
  border: "0.5px solid var(--color-border-secondary)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-primary)",
  fontFamily: "var(--font-sans)",
};

const errorBox: CSSProperties = {
  fontSize: 12,
  color: "var(--color-text-danger)",
  background: "var(--color-background-danger)",
  border: "0.5px solid var(--color-border-danger)",
  borderRadius: "var(--border-radius-sm)",
  padding: "8px 10px",
};

const footer: CSSProperties = { display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 4 };

const cancelBtn: CSSProperties = {
  padding: "7px 14px",
  fontSize: 12,
  borderRadius: "var(--border-radius-sm)",
  border: "0.5px solid var(--color-border-secondary)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const primaryBtn: CSSProperties = {
  padding: "7px 14px",
  fontSize: 12,
  borderRadius: "var(--border-radius-sm)",
  border: "none",
  background: "var(--color-text-primary)",
  color: "var(--color-background-primary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
  fontWeight: 500,
};
